import { useCallback, useState } from "react";
import { Linking, Platform } from "react-native";
import * as Sentry from "@sentry/react-native";
import { useGetGcpSignedUrlForReadLazyQuery } from "@axio/graphql/generated/graphql";

import type { ActivityAttachment } from "../types/reviewTypes";
import { isAttachmentDownloadable, isAttachmentOpenAction } from "../utils/attachments";
import { submitAuthenticatedHiddenDownloadForm } from "../utils/webFileDownload";

function sanitizeFileName(fileName: string): string {
  const sanitizedFileName = [...fileName]
    .filter((character) => {
      const characterCode = character.charCodeAt(0);
      return characterCode >= 32 && characterCode !== 127 && character !== '"' && character !== "\\";
    })
    .join("")
    .trim();

  return sanitizedFileName || "attachment";
}

function getResponseDisposition(disposition: "attachment" | "inline", fileName: string): string {
  const sanitizedFileName = sanitizeFileName(fileName);
  const asciiFileName = sanitizedFileName.replace(/[^\x20-\x7E]/g, "_");
  return `${disposition}; filename="${asciiFileName}"; filename*=UTF-8''${encodeURIComponent(sanitizedFileName)}`;
}

async function triggerWebDownload(attachment: ActivityAttachment): Promise<void> {
  await submitAuthenticatedHiddenDownloadForm(
    `/api/file/${encodeURIComponent(attachment.id)}/download`,
    `attachment-download-${attachment.id}-${Date.now()}`,
    {
      disposition: "attachment",
    },
    "Missing access token for attachment download"
  );
}

async function triggerWebDownloadAll(attachments: ActivityAttachment[]): Promise<void> {
  await submitAuthenticatedHiddenDownloadForm(
    `/api/file/download-all`,
    `attachment-download-all-${Date.now()}`,
    {
      fileIds: JSON.stringify(attachments.map((attachment) => attachment.id)),
    },
    "Missing access token for attachment download"
  );
}

async function openUrl(url: string): Promise<void> {
  if (Platform.OS === "web" && typeof window !== "undefined") {
    window.open(url, "_blank", "noopener,noreferrer");
    return;
  }

  await Linking.openURL(url);
}

function useAttachmentDownload() {
  const [getGcpSignedUrlForRead] = useGetGcpSignedUrlForReadLazyQuery();
  const [busyAttachmentIds, setBusyAttachmentIds] = useState<ReadonlySet<string>>(new Set());
  const [isDownloadingAll, setIsDownloadingAll] = useState(false);

  const setAttachmentBusy = useCallback((attachmentId: string, busy: boolean) => {
    setBusyAttachmentIds((currentIds) => {
      const nextIds = new Set(currentIds);
      if (busy) {
        nextIds.add(attachmentId);
      } else {
        nextIds.delete(attachmentId);
      }
      return nextIds;
    });
  }, []);

  const getSignedUrl = useCallback(
    async (attachment: ActivityAttachment, disposition: "attachment" | "inline") => {
      const result = await getGcpSignedUrlForRead({
        variables: {
          fileId: attachment.id,
          responseDisposition: getResponseDisposition(disposition, attachment.fileName),
        },
      });

      return result.data?.getGcpSignedUrlForRead?.data?.[0]?.signedUrl ?? null;
    },
    [getGcpSignedUrlForRead]
  );

  const downloadOne = useCallback(
    async (attachment: ActivityAttachment) => {
      setAttachmentBusy(attachment.id, true);
      try {
        if (Platform.OS === "web") {
          await triggerWebDownload(attachment);
          return;
        }

        const signedUrl = await getSignedUrl(attachment, "attachment");
        if (!signedUrl) return;

        await Linking.openURL(signedUrl);
      } catch (error) {
        Sentry.captureException(error);
      } finally {
        setAttachmentBusy(attachment.id, false);
      }
    },
    [getSignedUrl, setAttachmentBusy]
  );

  const openOne = useCallback(
    async (attachment: ActivityAttachment) => {
      setAttachmentBusy(attachment.id, true);
      try {
        if (isAttachmentOpenAction(attachment) && attachment.url) {
          await openUrl(attachment.url);
          return;
        }

        const signedUrl = await getSignedUrl(attachment, "inline");
        if (!signedUrl) return;
        await openUrl(signedUrl);
      } catch (error) {
        Sentry.captureException(error);
      } finally {
        setAttachmentBusy(attachment.id, false);
      }
    },
    [getSignedUrl, setAttachmentBusy]
  );

  const downloadAll = useCallback(
    async (attachments: ActivityAttachment[]) => {
      setIsDownloadingAll(true);
      try {
        const downloadableAttachments = attachments.filter(isAttachmentDownloadable);
        if (downloadableAttachments.length === 0) {
          return;
        }

        if (Platform.OS === "web") {
          await triggerWebDownloadAll(downloadableAttachments);
          return;
        }

        for (const attachment of downloadableAttachments) {
          await downloadOne(attachment);
        }
      } catch (error) {
        Sentry.captureException(error);
      } finally {
        setIsDownloadingAll(false);
      }
    },
    [downloadOne]
  );

  return {
    downloadOne,
    openOne,
    downloadAll,
    isDownloadingAll,
    isAttachmentBusy: (attachmentId: string) => busyAttachmentIds.has(attachmentId),
  };
}

export default useAttachmentDownload;
